import {
    type CSSProperties,
    type FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDragLayer } from "react-dnd";
import { GridCursor, type GridCursorType } from "./Cursor/GridCursor";
import { GridDropLayer } from "./GridDropLayer";
import {
    GRID_CURSOR_DEPTH,
    GRID_ON_TOP_DEPTH,
    GRID_SELECTED_DEPTH,
    type GridEditorProps,
    type GridValidCallback,
} from "./GridEditor";
import { GridEditorContext } from "./GridEditorBoundary";
import { GridItem } from "./GridItem";

export const GridEditorContent: FC<GridEditorProps> = ({
    width,
    height,
    columnSize,
    rowSize,
    items,
    onChange,
    onValid,
    renderItem,
    selected,
    onSelect,
    onDelete,
    wrapperClass,
    highlight,
}) => {
    const selectedRef = useRef<string | null>(null);
    const onTopRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);

    const [ cursor, setCursor ] = useState<null | GridCursorType>(null);
    const [ valid, setValid ] = useState(typeof onValid === "undefined");

    const boundaryId = useContext(GridEditorContext);

    useEffect(() => {
        selectedRef.current = selected;

        if(selected) {
            const item = items.find(item => item.id === selected);

            if(item) {
                setCursor({
                    top   : item.top,
                    left  : item.left,
                    width : item.width,
                    height: item.height,
                });
            }
        } else {
            setCursor(null);
        }
    }, [ selected, items ]);

    const { dragging } = useDragLayer(monitor => ({
        dragging: monitor.isDragging(),
    }));

    useEffect(() => {
        if(dragging) {
            document.body.classList.add("select-none");
        }

        // Timeout used to wait for all the event listeners in the drop event to fire
        setTimeout(() => {
            draggingRef.current = dragging;
        }, 1);

        return () => {
            document.body.classList.remove("select-none");
        };
    }, [ dragging ]);

    // Add window listener to de-select cursor
    useEffect(() => {
        const listener = (e: MouseEvent) => {
            if(draggingRef.current || !selectedRef.current) {
                return;
            }

            if(!(e.target instanceof Element)) {
                return;
            }

            // Check to see if the event is within the boundary
            if(boundaryId) {
                if(!e.target.closest(`#${boundaryId}`)) {
                    return;
                }
            }

            if(e.target.closest(`#grid-item-${selectedRef.current}`)) {
                return;
            }

            if(e.target.closest("#grid-drop-layer")) {
                return;
            }

            if(e.target.closest(".grid-editor-ignore")) {
                return;
            }

            if(e.target.closest(".grid-cursor")) {
                return;
            }

            onSelect(null);
            setCursor(null);
        };

        window.addEventListener("click", listener);

        return () => {
            window.removeEventListener("click", listener);
        };
    }, [ onSelect, boundaryId ]);

    const onValidCallback = useMemo<undefined | GridValidCallback>(() => {
        if(typeof onValid !== "function") {
            return undefined;
        }

        return (id: string, cursor: GridCursorType): boolean => {
            const validity = onValid(id, cursor);

            setValid(validity);

            return validity;
        };
    }, [ onValid ]);

    const onEnd = useCallback((dropped: boolean) => {
        if(!dropped) {
            // This causes the cursor in GridCursor.tsx to reset
            setCursor(prevState => {
                if(prevState === null) {
                    return prevState;
                }

                return {
                    ...prevState,
                };
            });
        }
    }, []);

    const onDrop = useCallback((cursor: GridCursorType) => {
        if(!selectedRef.current) {
            return;
        }

        onChange(selectedRef.current, cursor);
    }, [ onChange ]);

    let highlightPosition: null | GridCursorType = null;

    if(highlight !== null && selected === null) {
        const highlightItem = items.find(item => item.id === highlight);

        if(highlightItem) {
            highlightPosition = {
                top   : highlightItem.top,
                left  : highlightItem.left,
                width : highlightItem.width,
                height: highlightItem.height,
            };
        }
    }

    const classes = [ "grid-editor grid isolate relative w-full h-full" ];

    if(wrapperClass) {
        classes.push(wrapperClass);
    }

    let gridColumnSize: string;
    let gridColumnHalf: string;

    if(typeof columnSize === "number") {
        gridColumnSize = `${columnSize}px`;
        gridColumnHalf = `${columnSize / 2}px`;
    } else {
        // 100_000 is 100 * 1000
        gridColumnSize = `${Math.round(100_000 / width) / 1000}%`;
        gridColumnHalf = "50%";
    }

    let gridRowSize: string;
    let gridRowHalf: string;

    if(typeof rowSize === "number") {
        gridRowSize = `${rowSize}px`;
        gridRowHalf = `${rowSize / 2}px`;
    } else {
        // 100_000 is 100 * 1000
        gridRowSize = `${Math.round(100_000 / height) / 1000}%`;
        gridRowHalf = "50%";
    }

    // Handle the On Top portal styling
    let onTopStyle: CSSProperties;

    if(cursor === null) {
        onTopStyle = {
            display: "none",
        };
    } else {
        onTopStyle = {
            display        : "flex",
            gridColumnStart: cursor.left,
            gridColumnEnd  : `span ${cursor.width}`,
            gridRowStart   : cursor.top,
            gridRowEnd     : `span ${cursor.height}`,
            zIndex         : dragging ? GRID_SELECTED_DEPTH : GRID_ON_TOP_DEPTH,
        };
    }

    return (
        <div
            className={classes.join(" ")}
            style={{
                gridTemplateColumns: `repeat(${width}, ${gridColumnSize})`,
                gridTemplateRows   : `repeat(${height}, ${gridRowSize})`,
                backgroundPosition : `${gridColumnHalf} ${gridRowHalf}`,
                backgroundSize     : `${gridColumnSize} ${gridRowSize}`,
            }}
        >
            {items.map(item => (
                <GridItem
                    key={item.id}
                    selected={selected}
                    onTopRef={onTopRef}
                    onSelect={(id, cursor) => {
                        onSelect(id);

                        if(typeof onValid === "function") {
                            setValid(onValid(id, cursor));
                        } else {
                            setValid(true);
                        }
                    }}
                    {...item}
                >
                    {renderItem(item)}
                </GridItem>
            ))}
            {highlightPosition && (
                <div
                    className="border-2 border-blue-500 bg-blue-500/10"
                    style={{
                        gridColumnStart: highlightPosition.left,
                        gridColumnEnd  : `span ${highlightPosition.width}`,
                        gridRowStart   : highlightPosition.top,
                        gridRowEnd     : `span ${highlightPosition.height}`,
                        zIndex         : GRID_CURSOR_DEPTH,
                    }}
                />
            )}
            {cursor !== null && selected !== null && (
                <GridCursor
                    selected={selected}
                    valid={valid}
                    onValid={onValidCallback}
                    onDrop={onDrop}
                    cursor={cursor}
                    onDelete={onDelete}
                    onEnd={onEnd}
                />
            )}
            <div
                ref={onTopRef}
                style={onTopStyle}
                className="justify-center items-center pointer-events-none"
            />
            <GridDropLayer
                width={width}
                height={height}
            />
        </div>
    );
};
